import { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { authService } from '../config/index';
import { User } from '../types/user';
import { Box, Center, Spinner, Text } from '@chakra-ui/react';
import { useNavigate } from 'react-router-dom';

interface AuthContextType {
  currentUser: User | null;
  isLoading: boolean;
  refreshAuth: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType>({
  currentUser: null,
  isLoading: true,
  refreshAuth: async () => {}
});

export const useAuth = () => useContext(AuthContext);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider = ({ children }: AuthProviderProps) => {
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();

  // Función para actualizar el estado de autenticación
  const refreshAuth = async () => {
    try {
      console.log("AuthProvider: Refrescando estado de autenticación");
      setIsLoading(true);
      const userData = await authService.getCurrentUser();
      
      if (userData) {
        console.log("AuthProvider: Usuario autenticado detectado:", userData.email);
        setCurrentUser(userData);
      } else {
        console.log("AuthProvider: No hay usuario autenticado");
        setCurrentUser(null);
      }
    } catch (error) {
      console.error("Error al obtener datos del usuario:", error);
      setCurrentUser(null);
    } finally {
      setIsLoading(false);
    }
  };

  // Escuchar eventos de autenticación
  useEffect(() => {
    console.log("AuthProvider: Configurando listeners de eventos de autenticación");
    
    // Evento disparado al iniciar sesión exitosamente
    const handleLoginSuccess = (event: CustomEvent) => {
      console.log("AuthProvider: Evento de login exitoso recibido", event.detail);
      if (event.detail && event.detail.user) {
        setCurrentUser(event.detail.user);
        setIsLoading(false);
        
        // Forzar la navegación al dashboard
        console.log("AuthProvider: Forzando navegación a /dashboard");
        navigate('/dashboard', { replace: true });
      }
    };
    
    // Evento disparado al cerrar sesión
    const handleLogout = () => {
      console.log("AuthProvider: Evento de logout recibido");
      setCurrentUser(null);
      navigate('/login', { replace: true });
    };
    
    // Evento para forzar actualización
    const handleForceUpdate = () => {
      console.log("AuthProvider: Evento de actualización forzada recibido");
      refreshAuth();
    };
    
    // Añadir listeners de eventos
    window.addEventListener('auth:login-success', handleLoginSuccess as EventListener);
    window.addEventListener('auth:logout', handleLogout);
    window.addEventListener('auth:force-update', handleForceUpdate);
    
    // Remover listeners al desmontar
    return () => {
      window.removeEventListener('auth:login-success', handleLoginSuccess as EventListener);
      window.removeEventListener('auth:logout', handleLogout);
      window.removeEventListener('auth:force-update', handleForceUpdate);
    };
  }, [navigate]);

  // Verificar autenticación al montar el componente
  useEffect(() => {
    console.log("AuthProvider: Verificando estado de autenticación inicial");
    
    const checkAuth = async () => {
      try {
        // SIEMPRE intentar usar localStorage primero para evitar expulsiones
        const storedUser = localStorage.getItem('currentUser');
        const storedToken = localStorage.getItem('authToken');
        
        if (storedUser && storedToken) {
          try {
            const userFromStorage = JSON.parse(storedUser);
            const tokenData = JSON.parse(atob(storedToken));
            const currentTime = Date.now();
            
            // Si el token aún es válido, usar datos de localStorage inmediatamente
            if (tokenData.exp > currentTime) {
              console.log("AuthProvider: Sesión válida encontrada en localStorage");
              setCurrentUser(userFromStorage);
              setIsLoading(false);
              
              // Verificar con BD en segundo plano SOLO si no estamos navegando
              const isNavigating = sessionStorage.getItem('isNavigating') === 'true';
              if (!isNavigating) {
                console.log("AuthProvider: Verificación en segundo plano con BD (no bloqueante)");
                authService.getCurrentUser().then(userData => {
                  if (userData) {
                    console.log("AuthProvider: Datos actualizados desde BD");
                    setCurrentUser(userData);
                  }
                }).catch(error => {
                  console.warn("AuthProvider: Error en verificación de fondo (ignorado):", error);
                });
              }
              return; // Usuario cargado exitosamente
            } else {
              console.log("AuthProvider: Token expirado, limpiando sesión");
              localStorage.removeItem('currentUser');
              localStorage.removeItem('authToken');
            }
          } catch (error) {
            console.error("AuthProvider: Error al parsear datos de localStorage:", error);
            localStorage.removeItem('currentUser');
            localStorage.removeItem('authToken');
          }
        }
        
        // Solo si NO hay datos válidos en localStorage, intentar con BD
        console.log("AuthProvider: No hay sesión en localStorage, verificando con BD");
        const userData = await authService.getCurrentUser();
        
        if (userData) {
          console.log("AuthProvider: Usuario verificado con BD:", userData.email);
          setCurrentUser(userData);
        } else {
          console.log("AuthProvider: No hay usuario autenticado");
          setCurrentUser(null);
        }
      } catch (error) {
        console.error("AuthProvider: Error en verificación inicial:", error);
        
        // Último recurso: intentar usar datos de localStorage aunque haya error
        const storedUser = localStorage.getItem('currentUser');
        const storedToken = localStorage.getItem('authToken');
        if (storedUser && storedToken) {
          try {
            const userFromStorage = JSON.parse(storedUser);
            const tokenData = JSON.parse(atob(storedToken));
            if (tokenData.exp > Date.now()) {
              console.log("AuthProvider: Recuperación de emergencia exitosa");
              setCurrentUser(userFromStorage);
              setIsLoading(false);
              return;
            }
          } catch (parseError) {
            console.error("AuthProvider: Error en recuperación de emergencia:", parseError);
          }
        }
        setCurrentUser(null);
      } finally {
        setIsLoading(false);
      }
    };
    
    checkAuth();
  }, []);

  // VERIFICACIÓN PERIÓDICA DESHABILITADA TEMPORALMENTE
  // Para solucionar el problema de logout al actualizar la página
  useEffect(() => {
    if (!currentUser) return;
    
    console.log("AuthProvider: Verificación periódica DESHABILITADA para evitar logout al actualizar");
    
    // Solo verificar si el usuario ha estado inactivo por mucho tiempo (más de 2 horas)
    const checkInactivity = () => {
      const lastActivity = localStorage.getItem('lastActivity');
      if (lastActivity) {
        const timeSinceActivity = Date.now() - parseInt(lastActivity);
        const twoHours = 2 * 60 * 60 * 1000;
        
        if (timeSinceActivity > twoHours) {
          console.log("AuthProvider: Usuario inactivo por más de 2 horas, cerrando sesión");
          setCurrentUser(null);
          localStorage.removeItem('currentUser');
          localStorage.removeItem('authToken');
          localStorage.removeItem('lastActivity');
          navigate('/login', { replace: true });
        }
      }
    };
    
    // Verificar inactividad solo cada 10 minutos
    const inactivityInterval = setInterval(checkInactivity, 10 * 60 * 1000);
    
    return () => {
      clearInterval(inactivityInterval);
    };
  }, [currentUser, navigate]);

  // Marcar el tiempo de carga de la página para evitar verificaciones inmediatas
  useEffect(() => {
    sessionStorage.setItem('pageLoadTime', Date.now().toString());
  }, []);

  // Exponer el contexto de autenticación
  const value = {
    currentUser,
    isLoading,
    refreshAuth
  };

  // Mostrar spinner mientras se carga la información de autenticación
  if (isLoading) {
    return (
      <Box minH="100vh">
        <Center h="100vh" flexDirection="column">
          <Spinner size="xl" color="red.500" mb={4} />
          <Text color="gray.600">
            Verificando credenciales...
          </Text>
        </Center>
      </Box>
    );
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
